import React, { useEffect, useMemo, useState } from 'react'
import Box from '@ui/Box'
import { Button, Modal, StepProps, Steps, StepsProps } from 'antd'
import Grid from '@ui/Grid'
import Head from 'next/head'
import styled from 'styled-components'
import { isMobile } from '@utils/index'

type Props = {
  open: boolean
  onClose: () => void
  preload?: string[]
  title: string
  steps: React.ReactNode[]
  submitBtnLabel?: string
  onSubmit?: () => void
  nextDisabled?: (step: number) => boolean
}

const OnboardingPopup = ({
  open,
  onClose,
  preload,
  title,
  steps,
  submitBtnLabel,
  onSubmit,
  nextDisabled,
}: Props) => {
  const [step, setStep] = useState(0)

  useEffect(() => {
    if (open) {
      setStep(0)
    }
  }, [open])

  const progressSteps = useMemo<StepProps[]>(
    () =>
      new Array(steps.length).fill(0).map((_1, idx) => {
        if (idx < step) {
          return {
            status: 'finish',
          }
        }

        if (idx === step) {
          return {
            status: 'process',
          }
        }

        return {
          status: 'wait',
          disabled: !!nextDisabled,
        }
      }, []),
    [steps, step, nextDisabled],
  )

  return (
    <>
      <Head>
        {preload?.map(url => (
          <link key={url} rel="preload" href={url} as="image" />
        ))}
      </Head>
      <Modal
        footer={[]}
        title={<Box fontSize={22}>{title}</Box>}
        open={open}
        width={850}
        onCancel={onClose}
        maskClosable={false}
        style={{ top: 20 }}
      >
        {!isMobile && progressSteps?.length > 1 && (
          <Box mb={16}>
            <Steps
              current={step}
              items={progressSteps}
              onChange={currentStep => {
                setStep(currentStep)
              }}
            />
          </Box>
        )}

        <Box fontSize={16}>{steps[step]}</Box>
        <Box mt={26}>
          <Grid flow="column" gap={16} justifyContent="center">
            {step > 0 && (
              <Box>
                <Button onClick={() => setStep(s => s - 1)}>Previous</Button>
              </Box>
            )}
            {step < steps.length - 1 && (
              <Box>
                <Button
                  type="primary"
                  onClick={() => setStep(s => s + 1)}
                  disabled={nextDisabled?.(step)}
                >
                  Next
                </Button>
              </Box>
            )}
            {step === steps.length - 1 && (
              <Button type="primary" onClick={onSubmit || onClose}>
                {submitBtnLabel || 'Let me try! 🚀'}
              </Button>
            )}
          </Grid>
        </Box>
      </Modal>
    </>
  )
}

export default OnboardingPopup

export const OnboardingPopupList = styled.ul`
  margin: 16px 0;
  padding: 0;
`
export const OnboardingPopupListItem = styled.li`
  list-style: none;
  padding-bottom: 16px;
`

export const OnboardingPopupImg = styled.img`
  max-width: 800px;
  width: 100%;
`
